
import React, { useState } from 'react';
import { 
  Table, 
  TableBody
} from '@/components/ui/table';
import { IoTGatewayTableHeader, IoTGatewayTableRow, IoTGatewayNoDataRow } from './table';
import TableFilters from './TableFilters';
import { useIoTGatewayFilters } from './hooks/useIoTGatewayFilters';

interface IoTGateway {
  deviceId: string;
  location: string;
  status: string;
  connectedDevices: number;
  uptimeHours: number;
  cpuUsage: number;
  memoryUsage: number;
  temperatureCelsius: number;
  firmwareVersion: string;
  networkStatus: {
    connectivityType: string;
    signalStrength: number;
    dataTransferred: number;
    lastPingTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface IoTGatewayTableProps {
  gateways: IoTGateway[];
  onSelectDevice: (deviceId: string) => void;
}

// Helper function to convert filterCategories array to the new Record format
const convertFilterCategoriesToRecord = (categories: { name: string; options: { value: string; label: string; count?: number }[] }[]): Record<string, { label: string; value: string; count?: number }[]> => {
  const result: Record<string, { label: string; value: string; count?: number }[]> = {};
  
  categories.forEach(category => {
    result[category.name] = category.options.map(option => ({
      label: option.label,
      value: option.value,
      count: option.count
    }));
  });
  
  return result;
};

const IoTGatewayTable: React.FC<IoTGatewayTableProps> = ({ 
  gateways,
  onSelectDevice 
}) => {
  // State for active filters
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    Location: [],
    Status: [],
    ConnectivityType: [],
    Temperature: [],
    Usage: []
  });

  // Use the custom hook to get filter categories and filtered gateways
  const { filterCategories, filteredGateways } = useIoTGatewayFilters(gateways, activeFilters);

  // Handle filter changes
  const handleFilterChange = (category: string, value: string) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      
      if (newFilters[category].includes(value)) {
        // Remove the filter if it's already active
        newFilters[category] = newFilters[category].filter(v => v !== value);
      } else {
        // Add the filter
        newFilters[category] = [...newFilters[category], value];
      }
      
      return newFilters;
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setActiveFilters({
      Location: [],
      Status: [],
      ConnectivityType: [],
      Temperature: [],
      Usage: []
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <TableFilters 
          categories={convertFilterCategoriesToRecord(filterCategories)}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
        {Object.values(activeFilters).some(filters => filters.length > 0) && (
          <div className="text-sm text-muted-foreground">
            Showing {filteredGateways.length} of {gateways.length} gateways
          </div>
        )}
      </div>
      
      <div className="rounded-md border overflow-auto flex-grow h-[calc(100vh-30rem)]">
        <Table>
          <IoTGatewayTableHeader />
          <TableBody>
            {filteredGateways.map((gateway) => (
              <IoTGatewayTableRow 
                key={gateway.deviceId}
                gateway={gateway}
                onSelectDevice={onSelectDevice}
              />
            ))}
            {filteredGateways.length === 0 && (
              <IoTGatewayNoDataRow isEmpty={gateways.length === 0} />
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default IoTGatewayTable;
